import React, { useEffect, useState, useRef } from "react";
import { Button, Spin, message } from "antd";
import { Base64 } from "js-base64";
import { getPayPalClientToken } from "../../api";
import { AmountDivideByHundred } from "../../helper";
import { H1Text, FlexContainer } from "../../design";
import SmallScreenView from "../smallscreen";
import { Hidden } from "react-awesome-styled-grid";
import { SpText } from "../../widgetDesign";
import GooglePayIcon from "../../assets/imgs/GOOGLE_PAY.svg";

type Props = {
  headerText: string,
  payment: Object;
  chargeByGooglePay: Function;
  accountGid: String,
  setTransactionModalOpen: Function,
  paypalGooglePayRecaptchaToken: String,
};

const PAYPAL_SDK = "https://www.paypal.com/sdk/js";
const GOOGLE_PAY_SDK = "https://pay.google.com/gp/p/js/pay.js";

const PayPalGooglePay = (props: Props) => {
  const {
    headerText,
    payment,
    chargeByGooglePay,
    accountGid,
    setTransactionModalOpen,
    paypalGooglePayRecaptchaToken = null,
  } = props;

  const [loading, setLoading] = useState(true);
  const [eligible, setEligible] = useState(false);

  const paymentsClientRef = useRef(null);
  const allowedPaymentMethodsRef = useRef(null);
  const merchantInfoRef = useRef(null);

  const loadScript = (src, attrs = {}) => {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      Object.entries(attrs).forEach(([k, v]) => script.setAttribute(k, v));
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  const onPaymentAuthorized = (paymentData) => {
    return new Promise(async (resolve) => {
      try {
        await chargeByGooglePay(paymentData);
        setTransactionModalOpen(false);
        resolve({ transactionState: "SUCCESS" });
      } catch (err) {
        setTransactionModalOpen(false);
        resolve({ transactionState: "ERROR" });
      }
    });
  };

  const getGooglePaymentsClient = () => {
    if (!paymentsClientRef.current) {
      paymentsClientRef.current = new window.google.payments.api.PaymentsClient({
        environment: process.env.REACT_APP_PAYPAL_GOOGLE_PAY_MODE || "TEST",
        paymentDataCallbacks: {
          onPaymentAuthorized,
        },
      });
    }
    return paymentsClientRef.current;
  };

  useEffect(() => {
    const init = async () => {
      try {
        const token = await getPayPalClientToken(accountGid);
        const decoded = JSON.parse(Base64.decode(token));

        const clientId = decoded.clientId;
        const merchantId = decoded.merchantId;

        await loadScript(
          `${PAYPAL_SDK}?components=googlepay&client-id=${clientId}&merchant-id=${merchantId}&currency=${payment?.currency?.name}`,
          {
            "data-client-token": decoded?.token?.client_token,
          }
        );

        await loadScript(GOOGLE_PAY_SDK);

        const interval = setInterval(async () => {
          if (window.paypal && window.google?.payments?.api) {
            clearInterval(interval);

            const config = await window.paypal.Googlepay().config();
            allowedPaymentMethodsRef.current = config.allowedPaymentMethods;
            merchantInfoRef.current = config.merchantInfo;

            const client = getGooglePaymentsClient();

            const isReady = await client.isReadyToPay({
              apiVersion: config.apiVersion,
              apiVersionMinor: config.apiVersionMinor,
              allowedPaymentMethods: allowedPaymentMethodsRef.current,
            });

            setEligible(isReady.result && config.isEligible);
            setLoading(false);
          }
        }, 50);

      } catch (err) {
        console.error(err);
        setLoading(false);
        setEligible(false);
      }
    };

    init();
  }, []);

  const handleGooglePayClick = async () => {
    try {
      const allowedPaymentMethods = allowedPaymentMethodsRef.current;
      const merchantInfo = merchantInfoRef.current;
      if (!allowedPaymentMethods || !merchantInfo) {
        message.error("Google Pay not initialized");
        return;
      }

      const amountValue = AmountDivideByHundred(payment.amount);

      if (!amountValue) {
        message.error("Invalid amount");
        return;
      }

      const client = getGooglePaymentsClient();
      const isReady = await client.isReadyToPay({
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods,
      });

      if (!isReady.result) {
        message.error("Google Pay not available");
        return;
      }

      setTransactionModalOpen(true);

      const paymentDataRequest = {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods,
        merchantInfo,
        transactionInfo: {
          totalPriceStatus: "FINAL",
          totalPrice: String(amountValue),
          currencyCode: payment.currency.name,
          countryCode: payment.currency.countryAlpha2,
        },
        emailRequired: true,
        callbackIntents: ["PAYMENT_AUTHORIZATION"],
      };

      await client.loadPaymentData(paymentDataRequest);

    } catch (err) {
      console.error("Google Pay Error:", err);
      setTransactionModalOpen(false);
      message.error(err?.message || "Google Pay Failed");
    }
  };

  useEffect(() => {
    if (paypalGooglePayRecaptchaToken) {
      handleGooglePayClick();
    }
  }, [paypalGooglePayRecaptchaToken]);

  if (loading) return <Spin spinning tip="Please wait..." />;
  if (!eligible) return <SpText>Not Eligible!</SpText>;

  return (
    <>
      <H1Text className="px-4 mb-0">
        <SmallScreenView>{"Pay With Google Pay"}</SmallScreenView>
        <Hidden sm xs>{headerText || "Pay With Google Pay"}</Hidden>
      </H1Text>

      <FlexContainer flexDirection="column">
        <div className="px-4 mt-4 mb-4 w-100">
          <Button
            size="large"
            onClick={handleGooglePayClick}
            style={{
              background: "#000",
              color: "#fff",
              borderColor: "#000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <img src={GooglePayIcon} alt="Google Pay" />
            Pay with Google Pay
          </Button>
        </div>
      </FlexContainer>
    </>
  );
};

export default PayPalGooglePay;